import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, CheckCircle2, CloudOff, FolderSync, Loader2 } from "lucide-react";

// Auto-managed Cloud Sync status for Assistant (self-hosted) + SaaS. Read-only: detects the tenant's
// Entra Cloud Sync provisioning app via Graph and activates on-demand provisioning automatically.
// No manual enable/disable — "Check configuration" re-probes and enables it if found.
export function CloudSyncStatus({ s, t }) {
  const live = s?.mode === "live";
  const [status, setStatus] = useState(() => ({
    enabled: !!s?.cloudSync?.enabled,
    detected: s?.cloudSync?.detected ?? !!s?.cloudSync?.servicePrincipalId,
    spName: s?.cloudSync?.servicePrincipalName || s?.cloudSync?.servicePrincipalId || "",
    jobId: s?.cloudSync?.jobId || "",
    detectedAt: s?.cloudSync?.detectedAt || null,
  }));
  const [busy, setBusy] = useState(false);
  const auto = useRef(false);
  const inflight = useRef(false);
  const lastRun = s?.cloudSync?.lastRun || null;

  const check = useCallback(async (silent) => {
    if (inflight.current) return;   // dedupe concurrent probes (auto-detect + consent re-check race)
    inflight.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/settings/cloud-sync/check");
      if (data.error) {
        // Transient Graph error — keep the current state, just inform.
        if (!silent) toast.info(data.message);
      } else {
        setStatus({ enabled: data.enabled, detected: data.found,
          spName: data.servicePrincipalName || "", jobId: data.jobId || "",
          detectedAt: new Date().toISOString() });
        if (!silent) (data.found ? toast.success(data.message) : toast.info(data.message));
      }
    } catch (e) {
      if (!silent) toast.error(apiErr(e));
    } finally { setBusy(false); inflight.current = false; }
  }, []);

  // Auto-detect once when Graph is live and we've never probed this tenant.
  useEffect(() => {
    if (live && !status.detectedAt && !auto.current) { auto.current = true; check(true); }
  }, [live, status.detectedAt, check]);

  // Auto re-check the moment Graph admin consent completes (fired by the consent/recheck flows).
  useEffect(() => {
    const onRecheck = () => check(false);
    window.addEventListener("cloud-sync-recheck", onRecheck);
    return () => window.removeEventListener("cloud-sync-recheck", onRecheck);
  }, [check]);

  if (!live) return null;

  const runAgo = lastRun?.at ? new Date(lastRun.at).toLocaleString() : null;

  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid="cloud-sync-status">
      <div className="mb-3 flex items-center gap-2">
        <FolderSync size={16} className="text-primary" />
        <h3 className="font-display text-sm font-black uppercase tracking-tight text-zinc-900">{t("cloudsync.title")}</h3>
        <span className="ml-1 rounded-sm border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("cloudsync.auto")}</span>
      </div>

      {status.enabled ? (
        <div className="space-y-2 rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="cloud-sync-active">
          <div className="flex items-center gap-2 font-bold"><CheckCircle2 size={15} /> {t("cloudsync.active")}</div>
          {status.spName && <div><span className="text-emerald-600/80">{t("cloudsync.sp")}:</span> <span className="font-mono">{status.spName}</span></div>}
          {status.jobId && <div><span className="text-emerald-600/80">{t("cloudsync.job")}:</span> <span className="font-mono">{status.jobId}</span></div>}
          {lastRun && runAgo && (
            <div className="mt-1 flex items-center gap-1.5 border-t border-emerald-200/70 pt-1.5" data-testid="cloud-sync-lastrun">
              {lastRun.ok
                ? <CheckCircle2 size={13} className="text-emerald-600" />
                : <CloudOff size={13} className="text-amber-600" />}
              <span className={lastRun.ok ? "text-emerald-700" : "text-amber-700"}>
                {t("cloudsync.lastRun")}: {lastRun.ok ? t("cloudsync.runOk") : t("cloudsync.runFail")} · {runAgo}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600" data-testid="cloud-sync-inactive">
          <CloudOff size={15} className="mt-0.5 shrink-0 text-zinc-400" />
          <span>{status.detectedAt ? t("cloudsync.cloudOnly") : t("cloudsync.notChecked")}</span>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => check(false)} disabled={busy} data-testid="cloud-sync-check" className="rounded-sm border-zinc-300">
          {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />} {t("cloudsync.check")}
        </Button>
        <span className="text-[11px] text-zinc-400">{t("cloudsync.checkHint")}</span>
      </div>
    </div>
  );
}
